import { useEffect, useLayoutEffect, useRef } from "react";
import { SceneClock, sceneTimeBus } from "../scene_clock.es6";

// Calls `sample(el, t)` from the scene clock without a React render per
// frame. Without a scene time it runs a local, pausable clock.
export function useAnimationSampler(
  ref,
  sample,
  { seekTimeSec = null, timeOriginSec = null, sceneId = null, isPaused = false, active = true }
) {
  const latest = useRef(sample);
  latest.current = sample;
  const clock = useRef(null);
  if (!clock.current) clock.current = new SceneClock();
  const deterministic = seekTimeSec != null || timeOriginSec != null;
  useLayoutEffect(() => {
    if (!ref.current) return;
    const t =
      timeOriginSec != null && sceneTimeBus.sceneId === sceneId
        ? sceneTimeBus.sec - timeOriginSec
        : seekTimeSec;
    latest.current(ref.current, deterministic ? (t ?? 0) : clock.current.positionSec());
  });
  useEffect(() => {
    if (timeOriginSec == null || sceneId == null) return undefined;
    return sceneTimeBus.subscribe((sec, sid) => {
      if (sid === sceneId && ref.current) latest.current(ref.current, sec - timeOriginSec);
    });
  }, [ref, sceneId, timeOriginSec]);
  useEffect(() => {
    if (deterministic) return undefined;
    const timer = clock.current;
    if (!active) {
      timer.reset();
      if (ref.current) latest.current(ref.current, 0);
      return undefined;
    }
    if (isPaused) return undefined;
    timer.start();
    let raf;
    const tick = () => {
      if (ref.current) latest.current(ref.current, timer.positionSec());
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      timer.pause();
      cancelAnimationFrame(raf);
    };
  }, [ref, deterministic, active, isPaused]);
}
